import "./AppInstance.scss"

import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react"

import { AppInstance } from "@/entities/app/app.types"
import { MenuAppInstance } from "@/entities/app/menu/menu.types"
import OCBAppDAO from "@/entities/app/ocb/ocb.dao"
import Modal from "@/services/Modal/Modal"
import Expander from "@/shared/components/extrinsic/Expander/Expander"
import ButtonIcon from "@/shared/components/intrinsic/Button/ButtonIcon"
import FieldAdaptive from "@/shared/components/intrinsic/Field/FieldAdaptive"
import Icon from "@/shared/components/intrinsic/Icon/Icon"
import { classWithModifiers, inputValue, toggleState } from "@/utils/common"

import PopupAppInstanceDeletionConfirm from "../../modals/PopupAppInstanceDeletionConfirm"


export interface MenuAppInstanceViewProps extends AppInstance {
  name: string
  label?: string
  onNameChange?(value: string): void | Promise<void>

  active: boolean
  onActiveChange?(active: boolean): void

  editing?: boolean
  onEdit?(): void

  onDelete?(): void

  children?: ReactNode

  onCreate(): void | Promise<void>

  refreshInstance(): void | Promise<void>

  instances?: MenuAppInstance[]
}

const ocbAppDao = new OCBAppDAO

function MenuAppInstanceView(props: MenuAppInstanceViewProps) {
  const [expanded, setExpanded] = useState(true)

  function onDelete() {
    Modal.open(PopupAppInstanceDeletionConfirm, { onConfirm: props.onDelete })
  }

  return (
    <div className={classWithModifiers("app-instance", props.editing && "editing")}>
      <div className="app-instance__header">
        {props.children && <button type="button" onClick={toggleState(setExpanded)}><Icon name={expanded ? "chevron-up" : "chevron-down"} /></button>}
        <AppInstanceTitle value={props?.label || props.name} hoverValue={props.name} onChange={props.onNameChange} />
        <div className="app-instance__actions">
          <button
            type="button"
            onClick={props.refreshInstance}
            key={props.name}
            title="Refresh"
          >
            <Icon name="refresh" />
          </button>
          <div className="app-instance__toggle">
            {/* <Tumbler checked={props.active} readonly={props.onActiveChange == null} onChange={props.onActiveChange} /> */}
            {props.active && (
              <ButtonIcon size="smaller" name="plus" ariaLabel="Add menu app" await onClick={props.onCreate} />
            )}
          </div>
          <div className="app-instance__tools">
            {props.onEdit && (
              <button className="app-instance__button" type="button" onClick={props.onEdit}>Edit</button>
            )}
            {props.onDelete && (
              <ButtonIcon size="small" color="transparent" name="trash-bin" onClick={onDelete} ariaLabel="Delete" />
            )}
          </div>
        </div>
      </div>
      {props.children && <Expander expanded={expanded}><div className="app-instance__container">{props.children}</div></Expander>}
    </div>
  )
}

interface AppInstanceTitleProps {
  value?: string
  hoverValue?: string
  onChange?(value: string): void | Promise<void>
}

/**
 * Has optimistic update.
 */
function AppInstanceTitle(props: AppInstanceTitleProps) {
  const [value, setValue] = useState(props.value)
  const [editable, setEditable] = useState(false)
  const [initialValue, setInitialValue] = useState(props.value)

  const inputRef = useRef<HTMLInputElement>(null)

  async function onSubmit(value: string) {
    //await props.onChange?.(value)
    if (props.hoverValue && value) {
      const result = await ocbAppDao.updateName(props.hoverValue, value)
      if (result.name === value) {
        setInitialValue(value)
        setEditable(false)
      }
    }
  }

  useEffect(() => {
    if (editable) {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length)
    }
  }, [editable])

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (inputRef.current == null) return

    if (event.code === "Enter") {
      event.preventDefault()
      onSubmit(event.currentTarget.value)
      setEditable(false)

      return
    }

    if (event.code === "Escape") {
      event.preventDefault()
      setValue(props.value)
      setEditable(false)

      return
    }
  }

  return (
    <div className="app-instance-title">
      <FieldAdaptive className="app-instance-title__input" disabled={!editable} value={value} title={props.hoverValue} onChange={inputValue(setValue)} onKeyDown={onKeyDown} _ref={inputRef} />
      {!editable && <ButtonIcon size="small" color="transparent" name="pen" onClick={() => { setEditable(true) }} ariaLabel="Edit POS Config" />}
      {editable && <ButtonIcon size="small" color="transparent" name="cross-circle" onClick={() => { setEditable(false); setValue(initialValue) }} ariaLabel="Edit POS Config" />}
      {editable && <ButtonIcon disabled={((!value && value?.trim().length === 0) || value === initialValue || value?.trim().length === 0)} size="small" color="transparent" name="check" onClick={() => onSubmit(value!)} ariaLabel="Edit POS Config" />}
      {/* <button className={classWithModifiers("app-instance-title__toggle", editable && "editable")} type="button" onClick={toggleState(setEditable)}>
        <Icon name="pen" />
      </button> */}
    </div>
  )
}

export default MenuAppInstanceView
